import React, { useEffect, useState } from 'react';
import { subscribeDebugLog } from '../utils/debugLog';

// Realtime toggle helpers
export const isRealtimeEnabled = () => {
  try { return localStorage.getItem('ul_enable_realtime') === '1'; }
  catch { return false; }
};

export const setRealtimeEnabled = (on) => {
  try {
    localStorage.setItem('ul_enable_realtime', on ? '1' : '0');
  } catch {}
  window.location.reload();
};

// Session recording toggle helpers
export const isSessionRecordingEnabled = () => {
  try { return localStorage.getItem('ul_session_recording') === '1'; }
  catch { return false; }
};

export const setSessionRecordingEnabled = (on) => {
  try {
    localStorage.setItem('ul_session_recording', on ? '1' : '0');
  } catch {}
  window.location.reload();
};

export default function DebugOverlay({ visible, onClose, extraState, streakInfo }) {
  const [entries, setEntries] = useState([]);
  const [realtimeOn, setRealtimeOn] = useState(isRealtimeEnabled());
  const [recordingOn, setRecordingOn] = useState(isSessionRecordingEnabled());

  useEffect(() => {
    const unsubscribe = subscribeDebugLog(setEntries);
    return unsubscribe;
  }, []);

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-black/70 text-xs text-gray-100 flex flex-col">
      <div className="flex items-center justify-between px-4 py-2 bg-gray-900 border-b border-gray-700">
        <div className="font-semibold">
          🛠️ Unity Love Debug Console
        </div>
        <div className="flex items-center gap-3">
          {extraState && (
            <span className="text-[10px] text-gray-400 font-mono">
              {extraState}
            </span>
          )}
          <button
            onClick={onClose}
            className="px-3 py-1 rounded bg-red-600 hover:bg-red-700 text-white text-xs font-semibold"
          >
            Close (Ctrl+Alt+D)
          </button>
        </div>
      </div>

      {/* Feature Toggles Section */}
      <div className="px-4 py-2 bg-gray-900 border-b border-gray-700 flex items-center gap-4">
        <span className="text-[10px] text-gray-400 font-semibold uppercase">Toggles:</span>
        
        {/* Realtime Toggle */}
        <button
          onClick={() => setRealtimeEnabled(!realtimeOn)}
          className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${
            realtimeOn 
              ? 'bg-green-600 hover:bg-green-700 text-white' 
              : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
          }`}
        >
          Realtime: {realtimeOn ? 'ON' : 'OFF'}
        </button>
        
        {/* Session Recording Toggle */}
        <button
          onClick={() => setSessionRecordingEnabled(!recordingOn)}
          className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${
            recordingOn 
              ? 'bg-green-600 hover:bg-green-700 text-white' 
              : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
          }`}
        >
          Recording: {recordingOn ? 'ON' : 'OFF'}
        </button>
        
        <span className="text-[10px] text-gray-500 ml-2">
          (clicking reloads page)
        </span>
        
        {/* Streak + Unity Flow Debug Info */}
        {streakInfo && (
          <div className="ml-4 pl-4 border-l border-gray-600 flex items-center gap-2 flex-wrap">
            <span className="text-[10px] text-gray-400 font-semibold uppercase">Streak:</span>
            <span className="text-yellow-400 font-mono">
              🔥 {streakInfo.streak_current || 0}
            </span>
            <span className="text-[10px] text-gray-500">
              (best: {streakInfo.streak_best || 0})
            </span>
            <span className="text-[10px] text-gray-500">
              last: {streakInfo.streak_last_date || 'never'}
            </span>
            {streakInfo.streak_server_date && (
              <span className="text-[10px] text-blue-400">
                UTC: {streakInfo.streak_server_date}
              </span>
            )}
            {/* Unity Flow GO Progress */}
            <span className="text-[10px] text-purple-400 ml-2">
              GO: {streakInfo.unity_flow_go_count || 0}/3
            </span>
            {streakInfo.unity_flow_completed_today && (
              <span className="text-[10px] text-green-400">
                ✓ done
              </span>
            )}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-auto font-mono px-3 py-2 space-y-1 bg-gray-950">
        {entries.length === 0 && (
          <div className="text-center text-gray-500 py-8">
            No debug logs yet. Interact with the app to see events here.
          </div>
        )}
        {entries.slice().reverse().map((e) => (
          <div key={e.id} className="border-b border-gray-800 pb-1 mb-1">
            <div className="flex justify-between items-center">
              <span className="text-[10px] text-gray-500">
                {new Date(e.ts).toLocaleTimeString()}
              </span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                e.source === 'webrtc' ? 'bg-blue-900/50 text-blue-300' :
                e.source === 'reactions' ? 'bg-pink-900/50 text-pink-300' :
                e.source === 'audio' ? 'bg-purple-900/50 text-purple-300' :
                e.source === 'chat' ? 'bg-green-900/50 text-green-300' :
                'bg-emerald-900/50 text-emerald-300'
              }`}>
                {e.source}
              </span>
            </div>
            <div className="text-white mt-0.5">{e.message}</div>
            {e.data && (
              <pre className="mt-1 text-[10px] text-gray-400 whitespace-pre-wrap break-all bg-gray-900/50 p-1.5 rounded">
                {JSON.stringify(e.data, null, 2)}
              </pre>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
